import type { Database } from '@shkola/db-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { classLabel } from './contexts';
import { must, useDb } from './db';
import { type CheckinMarks, OFFLINE_ACTIONS } from './offline';

export type AlarmKind = Database['public']['Enums']['alarm_kind'];
export type CheckinStatus = Database['public']['Enums']['checkin_status'];
export type AlarmSession = Database['public']['Tables']['alarm_sessions']['Row'];

/** While an alarm is on, everything refreshes every few seconds. */
const LIVE_MS = 4000;

/** The open session of a school, if any (checked every 15 s, every 4 s while active). */
export function useActiveAlarm(schoolId: string) {
  const db = useDb();
  return useQuery({
    queryKey: ['alarm', 'active', schoolId],
    refetchInterval: (q) => (q.state.data ? LIVE_MS : 15_000),
    queryFn: async () => {
      // `must` cannot infer «row or null» from maybeSingle, so unwrap by hand
      const { data, error } = await db
        .from('alarm_sessions')
        .select('*')
        .eq('school_id', schoolId)
        .is('ended_at', null)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

export function useAlarmHistory(schoolId: string) {
  const db = useDb();
  return useQuery({
    queryKey: ['alarm', 'history', schoolId],
    queryFn: async () => {
      const [sessions, shelterLessons] = await Promise.all([
        db
          .from('alarm_sessions')
          .select('*')
          .eq('school_id', schoolId)
          .not('ended_at', 'is', null)
          .order('started_at', { ascending: false })
          .limit(50),
        // lessons that went on in the shelter (the «lost time» of the report)
        db.from('lessons').select('date').eq('school_id', schoolId).eq('format', 'shelter'),
      ]);
      const lessons = must(shelterLessons);
      return must(sessions).map((s) => {
        const date = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Kyiv' }).format(
          new Date(s.started_at),
        );
        return {
          ...s,
          minutes: Math.round(
            (Date.parse(s.ended_at as string) - Date.parse(s.started_at)) / 60_000,
          ),
          lessonsInShelter: lessons.filter((l) => l.date === date).length,
        };
      });
    },
  });
}

export interface ClassRollCall {
  classId: string;
  classLabel: string;
  grade: number;
  shelterId: string | null;
  shelterName: string | null;
  students: Array<{
    id: string;
    firstName: string;
    lastName: string;
    status: CheckinStatus | null;
  }>;
  /** students who learn from abroad: not in the building, not in the roll call */
  abroad: Array<{ id: string; firstName: string; lastName: string }>;
  confirmedAt: string | null;
  completedAt: string | null;
}

/** Everything the dashboard and the roll call need for one session. */
export function useAlarmBoard(schoolId: string, sessionId: string | undefined, live: boolean) {
  const db = useDb();
  return useQuery({
    queryKey: ['alarm', 'board', sessionId],
    enabled: sessionId !== undefined,
    refetchInterval: live ? LIVE_MS : false,
    queryFn: async () => {
      const [classes, shelters, enrollments, rollCalls, checkins, abroadRows] = await Promise.all([
        db
          .from('classes')
          .select('id, grade, letter, shelter_id')
          .eq('school_id', schoolId)
          .order('grade'),
        db.from('shelters').select('id, name, capacity').eq('school_id', schoolId).order('name'),
        db
          .from('class_enrollments')
          .select(
            'class_id, student:school_members!class_enrollments_school_id_student_member_id_fkey(id, first_name, last_name)',
          )
          .eq('school_id', schoolId),
        db
          .from('alarm_roll_calls')
          .select('*')
          .eq('session_id', sessionId as string),
        db
          .from('alarm_checkins')
          .select('student_member_id, status')
          .eq('session_id', sessionId as string),
        db.from('abroad_students').select('student_member_id').eq('school_id', schoolId),
      ]);
      const abroad = new Set(must(abroadRows).map((r) => r.student_member_id));
      const shelterList = must(shelters);
      const statusOf = new Map(must(checkins).map((c) => [c.student_member_id, c.status]));
      const calls = must(rollCalls);
      // a teacher sees only their own classes' students: other rows come without the person
      const people = must(enrollments).flatMap((p) =>
        p.student ? [{ classId: p.class_id, student: p.student }] : [],
      );
      const board: ClassRollCall[] = must(classes).map((c) => {
        const call = calls.find((r) => r.class_id === c.id);
        const shelterId = call?.shelter_id ?? c.shelter_id;
        return {
          classId: c.id,
          classLabel: classLabel(c),
          grade: c.grade,
          shelterId,
          shelterName: shelterList.find((s) => s.id === shelterId)?.name ?? null,
          students: people
            .filter((p) => p.classId === c.id && !abroad.has(p.student.id))
            .map((p) => ({
              id: p.student.id,
              firstName: p.student.first_name,
              lastName: p.student.last_name,
              status: statusOf.get(p.student.id) ?? null,
            }))
            .sort((a, b) => a.lastName.localeCompare(b.lastName, 'uk')),
          abroad: people
            .filter((p) => p.classId === c.id && abroad.has(p.student.id))
            .map((p) => ({
              id: p.student.id,
              firstName: p.student.first_name,
              lastName: p.student.last_name,
            })),
          confirmedAt: call?.confirmed_at ?? null,
          completedAt: call?.completed_at ?? null,
        };
      });
      return {
        classes: board,
        shelters: shelterList.map((s) => ({
          ...s,
          occupied: board
            .filter((c) => c.shelterId === s.id)
            .flatMap((c) => c.students)
            .filter((st) => st.status === 'in_shelter').length,
        })),
      };
    },
  });
}

/** A child's status in the open session (families). */
export function useChildAlarmStatus(sessionId: string | undefined, studentId: string) {
  const db = useDb();
  return useQuery({
    queryKey: ['alarm', 'child', sessionId, studentId],
    enabled: sessionId !== undefined,
    refetchInterval: LIVE_MS,
    queryFn: async () => {
      const { data, error } = await db
        .from('alarm_checkins')
        .select('status, marked_at')
        .eq('session_id', sessionId as string)
        .eq('student_member_id', studentId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

export function useAlarmActions() {
  const db = useDb();
  const client = useQueryClient();
  const invalidate = () => client.invalidateQueries({ queryKey: ['alarm'] });
  return {
    start: useMutation({
      mutationFn: async (a: { schoolId: string; kind: AlarmKind }) =>
        must(await db.rpc('start_alarm', { p_school: a.schoolId, p_kind: a.kind })),
      onSettled: () => {
        void invalidate();
        void client.invalidateQueries({ queryKey: ['notifications'] });
        void client.invalidateQueries({ queryKey: ['lessons'] });
      },
    }),
    end: useMutation({
      mutationFn: async (sessionId: string) =>
        must(await db.rpc('end_alarm', { p_session: sessionId })),
      onSettled: invalidate,
    }),
    /** «Ми в укритті» for a class. */
    confirm: useMutation({
      mutationFn: async (a: {
        schoolId: string;
        sessionId: string;
        classId: string;
        shelterId: string | null;
      }) =>
        must(
          await db.from('alarm_roll_calls').upsert(
            {
              school_id: a.schoolId,
              session_id: a.sessionId,
              class_id: a.classId,
              shelter_id: a.shelterId,
            },
            { onConflict: 'session_id,class_id' },
          ),
        ),
      onSettled: invalidate,
    }),
    complete: useMutation({
      mutationFn: async (a: { sessionId: string; classId: string }) =>
        must(
          await db
            .from('alarm_roll_calls')
            .update({ completed_at: new Date().toISOString() })
            .eq('session_id', a.sessionId)
            .eq('class_id', a.classId),
        ),
      onSettled: invalidate,
    }),
    /** One or many statuses at once (e.g. «all present are in the shelter»); works offline. */
    mark: useMutation<unknown, Error, CheckinMarks>({ mutationKey: OFFLINE_ACTIONS.markCheckin }),
  };
}

/** Minutes since a moment, for the live timer. */
export function minutesSince(iso: string, now = Date.now()): number {
  return Math.max(0, Math.floor((now - Date.parse(iso)) / 60_000));
}

export type DayFormat = 'online' | 'shelter' | 'in_person';

/** «Контрольний час»: classes (or the whole school) switch format for a day. */
export function useSetDayFormat(schoolId: string) {
  const db = useDb();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (a: { classIds: string[] | null; date: string; format: DayFormat }) =>
      must(
        await db.rpc('set_day_format', {
          p_school: schoolId,
          // null: every class of the school
          p_classes: a.classIds as string[],
          p_date: a.date,
          p_format: a.format,
        }),
      ),
    onSettled: () => client.invalidateQueries({ queryKey: ['lessons'] }),
  });
}
